// Editor visual del sitio — acceso a datos. SOLO endpoints de mcp_django
// (apps/site_config), todos con la org del JWT:
//
//   GET   /admin/site-config/pages                       páginas
//   GET   /admin/site-config/sections?page_slug=          lo PUBLICADO de una página
//   PATCH /admin/site-config/sections/{key}?page_slug=    crea una revisión (propone o aplica según la política)
//   GET   /admin/site-config/revisions?status=pending&page_slug=
//   POST  /admin/site-config/revisions/{id}/approve|reject   (solo humano admin)
//   POST  /admin/site-config/revisions/{id}/revert
//   GET   /admin/site-config/edit-policy
//   POST  /admin/site-config/preview-token {page_slug, parent_origin}   → token 30 min + url
//   GET   /public/site-config/preview?token=              site-config con las pendientes aplicadas
//
// La interfaz `SiteEditorApi` permite montar el editor con datos simulados en
// la página de desarrollo (src/dev/editor-sitio.html) sin tocar el backend.

import { djangoApiCall } from '@/lib/api';

export type Json = null | boolean | number | string | Json[] | { [k: string]: Json };
export type Config = { [k: string]: Json };

export interface PageInfo {
  page_slug: string;
  sections: number;
  enabled: number;
}

export interface SectionState {
  section_key: string;
  order: number;
  is_enabled: boolean;
  config: Config;
}

export interface PendingRevision {
  id: number;
  page_slug: string;
  section_key: string;
  op: string;
  status: string;
  summary: string;
  reason?: string;
  actor?: string;
  actor_kind: string;
  created_at?: string | null;
}

export interface EditResult {
  revision_id: number | null;
  status: 'applied' | 'pending' | 'unchanged' | string;
  summary: string;
  note?: string;
}

export interface PreviewToken {
  token: string;
  expires_at: string;
  ttl_seconds: number;
  host: string;
  page_slug: string;
  url: string;
}

export interface SectionPatch {
  set?: Record<string, Json>;
  is_enabled?: boolean;
  order?: number;
  reason: string;
  mode?: 'propose' | 'apply';
}

export interface SiteEditorApi {
  pages(): Promise<PageInfo[]>;
  published(page: string): Promise<SectionState[]>;
  /** Secciones de la página con las propuestas pendientes aplicadas (lo que muestra la vista previa). */
  draft(token: string, page: string): Promise<SectionState[]>;
  previewToken(page: string, parentOrigin: string): Promise<PreviewToken>;
  pending(page: string): Promise<PendingRevision[]>;
  editMode(): Promise<'propose' | 'apply'>;
  patch(page: string, key: string, body: SectionPatch): Promise<EditResult>;
  approve(id: number): Promise<EditResult>;
  reject(id: number): Promise<EditResult>;
  revert(id: number, reason: string): Promise<EditResult>;
}

const q = (page: string) => `page_slug=${encodeURIComponent(page)}`;

export const liveApi: SiteEditorApi = {
  pages: () => djangoApiCall<PageInfo[]>('/admin/site-config/pages'),
  published: (page) => djangoApiCall<SectionState[]>(`/admin/site-config/sections?${q(page)}`),
  draft: async (token, page) => {
    const cfg = await djangoApiCall<{ sections?: Array<{ key: string; page_slug?: string; order: number; is_enabled: boolean; config: Config }> }>(
      `/public/site-config/preview?token=${encodeURIComponent(token)}`,
    );
    return (cfg.sections ?? [])
      .filter((s) => (s.page_slug || '') === page)
      .map((s) => ({ section_key: s.key, order: s.order, is_enabled: s.is_enabled, config: s.config || {} }))
      .sort((a, b) => a.order - b.order);
  },
  previewToken: (page, parentOrigin) =>
    djangoApiCall<PreviewToken>('/admin/site-config/preview-token', {
      method: 'POST',
      body: JSON.stringify({ page_slug: page, parent_origin: parentOrigin }),
    }),
  pending: (page) => djangoApiCall<PendingRevision[]>(`/admin/site-config/revisions?status=pending&limit=100&${q(page)}`),
  editMode: async () => {
    const p = await djangoApiCall<{ edit_mode?: string }>('/admin/site-config/edit-policy');
    return p?.edit_mode === 'apply' ? 'apply' : 'propose';
  },
  patch: (page, key, body) =>
    djangoApiCall<EditResult>(`/admin/site-config/sections/${encodeURIComponent(key)}?${q(page)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  approve: (id) => djangoApiCall<EditResult>(`/admin/site-config/revisions/${id}/approve`, { method: 'POST' }),
  reject: (id) => djangoApiCall<EditResult>(`/admin/site-config/revisions/${id}/reject`, { method: 'POST' }),
  revert: (id, reason) =>
    djangoApiCall<EditResult>(`/admin/site-config/revisions/${id}/revert`, {
      method: 'POST',
      body: JSON.stringify({ reason, mode: 'apply' }),
    }),
};
